import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonCol,
  IonContent,
  IonHeader,
  IonItem,
  IonPage,
  IonSearchbar,
  IonTitle,
  IonToolbar
} from "@ionic/react";
import CoinListItem from "./CoinListItem";
import { useBotsStore } from "../stores/bots.store";
import { useCoinsStore } from "../stores/coins.store";
import { useEffect, useState } from "react";
import { globalWhitelist } from "../configs/whitelist";
import AnimationContainer from "./Animation";
import getCoinImage from "../utils/getCoinImage";
import { getCoinName } from "../utils/getCoinName";

interface CoinListProps {
  botId?: string;
  selectionMode?: boolean;
  whitelist?: string[];
  onDismiss?: (whitelist?: string[]) => void;
  options?: boolean;
}

const formatNumberToShorthand = (num: number): string => {
  if (!num) return "0";
  if (Math.abs(num) >= 1.0e12) {
    // Trillions
    return (num / 1.0e12).toFixed(4) + "T";
  } else if (Math.abs(num) >= 1.0e9) {
    // Billions
    return (num / 1.0e9).toFixed(4) + "B";
  } else if (Math.abs(num) >= 1.0e6) {
    // Millions
    return (num / 1.0e6).toFixed(4) + "M";
  } else if (Math.abs(num) >= 1.0e3) {
    // Thousands
    return (num / 1.0e3).toFixed(4) + "K";
  } else {
    return num.toString();
  }
};

const CoinList: React.FC<CoinListProps> = ({
  botId,
  selectionMode,
  whitelist,
  onDismiss,
  options
}) => {
  // TODO: FIX Selection or simplify this component further
  const [coinsNotInWhitelist, setCoinsNotInWhitelist] = useState<any[]>([]);
  const [coinsToTrack, setCoinsToTrack] = useState<any[]>([]);
  const [activeSort, setActiveSort] = useState(botId ? "quoteValue" : "price");
  const [showSmallBalances, setShowSmallBalances] = useState(options || false);
  const [coinFilter, setCoinFilter] = useState("");
  const [selectAll, setSelectAll] = useState(false);

  const coinMarketGlobal = useCoinsStore(
    (state: any) => state.coinMarketGlobal[0]
  );

  const bot = useBotsStore(state => state.bots.find(bot => bot.id === botId));
  const [newWhitelist, setNewWhitelist] = useState<string[]>(
    whitelist || bot?.whitelist || []
  );
  useEffect(() => {
    if (bot?.data?.portfolio) {
      const portfolio = bot.data.portfolio;
      setCoinsNotInWhitelist(
        portfolio?.coinBalances
          ?.filter(
            (cb: any) =>
              parseFloat(cb?.free) > 0 &&
              cb?.asset !== bot?.quoteAsset &&
              !bot.whitelist?.includes(cb?.asset)
          )
          ?.map((cb: any) => cb?.asset)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bot?.data?.portfolio]);

  useEffect(() => {
    const newCoinsToTrack = options
      ? [...(bot?.whitelist || [])]
      : [...(bot?.whitelist || []), ...coinsNotInWhitelist];
    if (newCoinsToTrack.length !== coinsToTrack.length) {
      setCoinsToTrack(newCoinsToTrack);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinsNotInWhitelist, options]);

  const [setActiveWebsockets, allCoins] = useCoinsStore(state => [
    state.setActiveWebsockets,
    Object.values(state.coins)?.filter(
      (coin: any) =>
        !botId || coinsToTrack?.includes(coin?.symbol?.replace("USDT", ""))
    )
  ]);

  const connectWebsockets = () => {
    const binanceTickers = globalWhitelist.map(
      (symbol: string) => `${symbol.toLowerCase()}usdt@ticker`
    );
    const activeWebsockets =
      (botId &&
        coinsToTrack?.map(
          (symbol: string) => `${symbol.toLowerCase()}usdt@ticker`
        )) ||
      binanceTickers;
    setActiveWebsockets(activeWebsockets);
  };

  useEffect(() => {
    if (botId && coinsToTrack.length === 0) return;
    // Connect websockets on first render
    connectWebsockets();

    // Set up visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Clean up on unmount
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      setActiveWebsockets([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coinsToTrack]);

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      // The page is visible again
      console.log("The page is now visible again");
      connectWebsockets();
    } else {
      // The page is hidden
      console.log("The page is now hidden");
      setActiveWebsockets([]);
    }
  };

  const balances = bot?.data?.portfolio?.coinBalances?.map((cb: any) => {
    const symbol: string = cb?.asset + bot?.quoteAsset;
    const balance: any = {
      balance: parseFloat(cb?.free),
      symbol
    };
    return balance;
  });

  const addBalanceAndQuoteValue = (coin: any) => {
    const balance = balances?.find((b: any) => b.symbol === coin.symbol);
    if (balance) {
      return {
        ...coin,
        balance: balance.balance,
        quoteValue: balance.balance * coin?.price
      };
    }
    if (!bot) {
      return coin;
    } else {
      return { ...coin, balance: 0, quoteValue: 0 };
    }
  };

  let coins = allCoins?.map(addBalanceAndQuoteValue);

  useEffect(() => {
    setSelectAll(newWhitelist.length === coins.length);
  }, [newWhitelist, coins.length]);

  const handleSelection = (coin: string) => {
    // setNewWhitelist(
    //   newWhitelist?.includes(coin)
    //     ? newWhitelist?.filter((c: string) => c !== coin)
    //     : [...(newWhitelist || []), coin]
    // );
    if (newWhitelist.includes(coin)) {
      setNewWhitelist(newWhitelist.filter(c => c !== coin));
    } else {
      setNewWhitelist([...newWhitelist, coin]);
    }
  };

  if (bot) {
    const usdtBalance = balances?.find(
      (b: any) => b.symbol === "USDTUSDT"
    )?.balance;
    coins.push({
      symbol: "USDTUSDT",
      balance: usdtBalance,
      price: 1,
      quoteValue: usdtBalance,
      changePercent: 0
    });
  }

  // Sort coins by price and add ranking
  coins.sort((a: any, b: any) => parseFloat(b.price) - parseFloat(a.price));
  coins.forEach((coin, i) => (coin.ranking = i + 1));

  // Sort coins by activeSort
  if (activeSort === "changePercent") {
    coins.sort(
      (a: any, b: any) => parseFloat(b[activeSort]) - parseFloat(a[activeSort])
    );
  } else {
    coins.sort((a: any, b: any) => b[activeSort] - a[activeSort]);
  }

  // Get top 3 coins based on changePercent
  const topCoins = [...coins]
    .sort(
      (a: any, b: any) =>
        parseFloat(b.changePercent) - parseFloat(a.changePercent)
    )
    .slice(0, 3);

  coins = coins?.filter((coin: any) =>
    coin.symbol.includes(coinFilter.toUpperCase())
  );
  if (botId) {
    coins = coins.filter((coin: any) =>
      showSmallBalances
        ? options
          ? coin
          : coin?.quoteValue > 0
        : coin?.quoteValue > 10
    );
  }

  const handleUpdateWhitelist = () => {
    onDismiss && onDismiss(newWhitelist);
  };

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setNewWhitelist([]);
    } else {
      setNewWhitelist(coins.map(coin => coin.symbol.replace("USDT", "")));
    }
  };
  const content = (
    <div>
      {!bot && (
        <>
          <div className="bg-primary-50 rounded-t-xl pt-10">
            <div className="px-4 pb-2 font-semibold">
              <div className="flex justify-between">
                <div className="text-2xl">Coin Market Cap</div>
                <div className="text-lg text-secondary-50">
                  24h{" "}
                  {coinMarketGlobal?.quote?.USD?.total_market_cap_yesterday_percentage_change?.toFixed(
                    2
                  )}
                  %
                </div>
              </div>
              <div className="text-4xl">
                $
                {formatNumberToShorthand(
                  coinMarketGlobal?.total_market_cap_usd
                )}
              </div>
            </div>

            <div className="bg-primary-80 py-4">
              <IonSearchbar
                onIonChange={e => setCoinFilter(e.target.value || "")}
              />
            </div>
          </div>
          <div className="px-4">
            <div className="py-2">Top performing coins</div>
            <div className="flex w-full justify-around gap-x-2">
              {topCoins.length > 0 &&
                topCoins.map((coin: any, i) => {
                  const coinId = coin.symbol.replace("USDT", "");
                  const percentClass =
                    coin.changePercent > 0
                      ? "text-green-400 bg-green-800"
                      : "text-red-400 bg-red-800";
                  return (
                    <div
                      key={i}
                      className="bg-primary-80 p-4 rounded-lg flex flex-col items-center w-full relative"
                    >
                      <div className="absolute top-0 left-0 bg-primary-90 text-secondary-50 p-1">
                        #{i + 1}
                      </div>
                      <img
                        src={getCoinImage(coinId)}
                        alt={coin}
                        className="w-[32px] h-[32px]"
                      />
                      <div className="font-semibold">{getCoinName(coinId)}</div>
                      <div className="text-sm">
                        ${" "}
                        {coin.price?.toLocaleString(undefined, {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </div>
                      <div
                        className={"rounded-md p-0.5 text-sm " + percentClass}
                      >
                        {parseFloat(coin.changePercent).toFixed(2)}%
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      )}
      <IonItem>
        <IonCol size="3" className="text-center text-slate-200 text-xs">
          COIN
        </IonCol>
        {bot && !options && (
          <>
            <IonCol
              size="4"
              className="text-center text-slate-200 text-xs cursor button"
              onClick={() => setActiveSort("balance")}
            >
              BALANCE
            </IonCol>
            <IonCol
              size="3"
              className="text-center text-slate-200 text-xs cursor button"
              onClick={() => setActiveSort("quoteValue")}
            >
              USDT
            </IonCol>
          </>
        )}
        {bot && options && (
          <>
            <IonCol
              size="3"
              className="text-center text-slate-200 text-xs cursor button"
              onClick={() => setActiveSort("price")}
            >
              PRICE
            </IonCol>
            <IonCol
              size="3"
              className="text-center text-slate-200 text-xs cursor button"
              onClick={() => setActiveSort("rsi")}
            >
              RSI
            </IonCol>
          </>
        )}
        {!bot && (
          <IonCol
            size="6"
            className="text-center text-slate-200 text-xs cursor button"
            onClick={() => setActiveSort("price")}
          >
            USDT
          </IonCol>
        )}
        <IonCol
          size="2"
          className="text-center text-slate-200 text-xs cursor button"
          onClick={() => setActiveSort("changePercent")}
        >
          24H%
        </IonCol>
        {selectionMode && (
          <IonCol size="1">
            <IonCheckbox
              checked={selectAll}
              onIonChange={e => {
                e.stopPropagation();
                // toggle all coins or remove all coins from the whitelist
                handleSelectAllToggle();
              }}
            />
          </IonCol>
        )}
      </IonItem>
      {coins.length === 0 && (
        <div>
          <AnimationContainer animationName="thinkingAnimation" />
          <div className="text-2xl font-semibold text-primary-50 text-center">
            Coins loading
          </div>
        </div>
      )}
      <div className="h-full overflow-y-auto"></div>
      {coins
        ?.sort((a: any, b: any) => (options ? a.ranking - b.ranking : 0))
        ?.map(
          (coin: any) =>
            (!options || (options && coin.symbol !== "USDTUSDT")) && (
              <CoinListItem
                key={coin.symbol}
                coin={coin}
                botId={botId}
                index={coin?.ranking}
                options={options}
                {...(selectionMode && {
                  selectionMode,
                  selected: newWhitelist?.includes(
                    coin.symbol.replace("USDT", "")
                  ),
                  onSelect: () =>
                    handleSelection(coin.symbol.replace("USDT", ""))
                })}
              />
            )
        )}
      {botId && !options && (
        <div className="flex justify-center py-4">
          <IonButton
            fill="outline"
            className="text-white text-base"
            expand="block"
            onClick={() => setShowSmallBalances(!showSmallBalances)}
          >
            {showSmallBalances ? "Hide" : "Show"} Small Balances
          </IonButton>
        </div>
      )}
    </div>
  );

  if (selectionMode) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => onDismiss && onDismiss(bot?.whitelist)}>
                Cancel
              </IonButton>
            </IonButtons>
            <IonTitle>Edit Coins</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleUpdateWhitelist}>Save</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">{content}</IonContent>
      </IonPage>
    );
  } else {
    // If not in selection mode, just render the content directly
    return content;
  }
};

export default CoinList;
